import BaseInteraction from './BaseInteraction';
import ContentItem from '../api/content/ContentItem';
import attachShakaEvents from './utils/attachShakaEvents';
import bindShakaToWebRTC from '../webrtc/bindShakaToWebRTC';
import logger from '../log';
import {
  PlayerNames,
  ShakaPlayer,
  ShakaPlugin
} from '@kollective-frontend/ksdk-api';
import { SourceSetEventOptions } from '../events';
import { SourceType } from '../common/constants';
import { TechApi } from '../bootloader/tech';
import { Types, checkType, logUnableToSetupMessage } from '../util/checkTypes';

const log = logger.getLogger(__filename);

declare global {
  interface Window {
    shaka: ShakaPlugin;
  }
}

export default class ShakaInteraction extends BaseInteraction<ShakaPlayer> {
  constructor(player: ShakaPlayer, tech: TechApi) {
    checkType(player, Types.SHAKA).emitErrorIfNotCorrectType();
    checkType(window, Types.SHAKA_WINDOW).emitErrorIfNotCorrectType();
    super(player, tech);
    this.cleanupMethods.push(attachShakaEvents(player, tech));
  }

  initialize(): void {
    super.initialize();
    const result = checkType(window, Types.SHAKA_WINDOW);
    if (result.isType) {
      bindShakaToWebRTC(this.player, this.binder);
    } else {
      logUnableToSetupMessage(PlayerNames.SHAKA_PLAYER, log, result);
    }
  }

  // NOTE: binding shaka binds to the window.shaka object so there is no need
  // for us to rebind the shaka player when someone cleans and reinstantiates
  // the shaka player, we just need to bind the media element.
  resetEventsAndInterceptors(opts: SourceSetEventOptions<ShakaPlayer>): void {
    this.player = opts.options?.player as ShakaPlayer;
    this.element = opts.options?.ele as HTMLVideoElement;
    this.cleanupMethods.push(attachShakaEvents(this.player, this.tech));
    this.addEventListeners();
  }

  loader(items: ContentItem[]): ContentItem[] {
    const [firstSrc] = items;
    // https://shaka-player-demo.appspot.com/docs/api/shaka.Player.html load method
    this.setSource(firstSrc.url);
    return [firstSrc];
  }

  setSource(url: string, hasRetried = false): Promise<void> {
    const result = checkType(this.player, Types.SHAKA);
    if (!result.isType) {
      const data = result.data || ({} as Record<string, string>);
      log.warn(data.reason, data.messages);
      return Promise.reject(new Error(data.reason));
    }
    log.debug(`setSource: ${url}`);

    if (this.player.getAssetUri() == url) {
      log.info(`player already has ${url} loaded`);
      return Promise.resolve();
    }

    const loadMode = this.player.getLoadMode();
    log.info(`Load mode is ${loadMode}`);
    // See LoadMode - https://shaka-player-demo.appspot.com/docs/api/shaka.Player.html
    // Error 7000 can get throw
    // TODO: See SRV-1054 load mode 0 (destroyed) should never get here
    return this.player.load(url).then(
      () => {
        log.debug(`Loaded ${url}`);
        return Promise.resolve();
      },
      err => {
        log.error(
          `Error when loading source ${url}. Shaka mode: ${loadMode} before and ${this.player.getLoadMode()} after.`,
          err
        );
        if (!hasRetried) {
          return new Promise((res, rej) => {
            // If we've seen an error that fails to load, wait 50 ms and retry setting the source.
            setTimeout(() => {
              this.setSource(url, true).then(res, rej);
            }, 50);
          });
        } else {
          return Promise.reject(err);
        }
      }
    );
  }

  clearSource(): void {
    if (typeof this.player.unload !== 'function') {
      log.warn('Unable to unload player as player.unload is not a function');
      return;
    }
    this.player.unload().then(
      () => {
        log.debug('Unloaded player media');
      },
      (err: Error) => {
        log.error('Error when unloading media.', err);
      }
    );
  }

  mediaElement(): HTMLMediaElement | undefined {
    if (typeof this.player?.getMediaElement !== 'function') {
      return this.element;
    }
    return this.player.getMediaElement() ?? this.element;
  }

  getAllowedSourceTypes(): SourceType[] {
    return [SourceType.HLS, SourceType.DASH];
  }
}
